<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" id="addEarningModal" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Position</h3>
        </template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="dataLoaded">
                <template v-for="i in 5">
                    <form-input v-if="i == client.location_org_level" :key="`location-${i}`" label="Location" type="select" :options="locationOptions" v-model="formData.work_location" :errors="errors.work_location"/>
                    <form-input v-else-if="orgOptionsByLevel[i] && orgOptionsByLevel[i].length" :key="`org-level-${i}`" :label="client[`org_level_${i}_label`]" type="select" :options="orgOptionsByLevel[i]" v-model="formData[`org_level_${i}_value`]" :errors="errors[`org_level_${i}_value`]"/>
                </template>
                <form-input :disabled="!formData.work_location" label="Pay Group" type="select" :options="paygroupOptions" v-model="formData.paygroup" :errors="errors.paygroup"/>

                <form-input :disabled="!formData.work_location" label="Delivery Address" type="select" :options="deliveryOptions" v-model="formData.delivery_address" :errors="errors.delivery_address"/>

                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.bank_account_net_pay" :errors="errors.bank_account_net_pay" label="Net Pay Bank Account" type="select" :options="bankAccountOptions" :noblank="true" />
                    </div>

                    <div class="col-6">
                        <form-input v-model="formData.bank_account_taxes" :errors="errors.bank_account_taxes" label="Taxes Bank Account" type="select" :options="bankAccountOptions" :noblank="true" />
                    </div>
                </div>

                <form-input v-model="formData.is_regularly_tipped" type="checkbox" :errors="errors.is_regularly_tipped" label="Regularly Tipped Position" helper-text="Employer can claim tip credit for this employee"></form-input>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    props: ['client', ],
    mixins: [FormMixin, ModalMixin],
    components: {},
    data() {
        return {
            formData: this.makeFormData(),
            bankAccounts: null,
            orgs: null,
            locations: null,
            paygroups: null,
            deliveries: null,
        }
    },
    mounted() {
        this.getData()
    },
    watch: {
        dataLoaded(val) {
            if (val) {
                this.$store.dispatch('STOP_LOADING')
            }
        }
    },
    computed: {
        dataLoaded() {
            return this.orgs && this.locations && this.paygroups && this.bankAccounts && this.deliveries
        },
        orgOptionsByLevel() {
            if (!this.dataLoaded) {return {}}

            let result = {}
            for (let i = 1; i <= 5; i++) {
                result[i] = []
                this.orgs.filter(o => o.org_level == i).forEach(o => {
                    result[i].push({text: o.org_level_description, value: o.org_level_description})
                })

                result[i].sort((a, b) => {
                    return (a.text > b.text) ? 1 : -1
                })
            }
            return result
        },
        locationOptions() {
            if (!this.dataLoaded) {return []}

            let result = []
            this.locations.forEach(l => {
                result.push({text: l.name, value: l.id})
            })

            result.sort((a, b) => {
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        paygroupOptions() {
            let result = []

            if (!this.dataLoaded || !this.formData.work_location) {return result}

            const location = this.locations.find(l => l.id == this.formData.work_location)
            const paygroups = this.paygroups.filter(p => p.company_id == location.company_id)
            paygroups.forEach(l => {
                result.push({text: l.name, value: l.id})
            })

            result.sort((a, b) => {
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        bankAccountOptions() {
            const result = [
                {text: '--Default--', value: ''}
            ]

            if (!this.dataLoaded || !this.formData.work_location) {return result}

            const location = this.locations.find(l => l.id == this.formData.work_location)
            const bankAccounts = this.bankAccounts.filter(b => b.company_id == location.company_id)

            bankAccounts.forEach(b => {
                result.push({text: b.name, value: b.id})
            })

            result.sort((a, b) => {
                if (!a.value) {return -1}
                if (!b.value) {return 1}
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        deliveryOptions() {
            const result = [
                {text: 'Use Default', value: 'default'}
            ]

            if (!this.dataLoaded || !this.formData.work_location) {return result}

            const location = this.locations.find(l => l.id == this.formData.work_location)
            const deliveries = this.deliveries.filter(d => d.company_id == location.company_id)

            deliveries.forEach(d => {
                result.push({text: d.name, value: d.id})
            })

            result.sort((a, b) => {
                if (a.value == 'default') {return -1}
                if (b.value == 'default') {return 1}
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
    },
    methods: {
        getOrgs() {
            return this.$api.get(`/clients/${this.client.id}/settings/company-orgs`).then(resp => {
                this.orgs = resp
            })
        },
        getLocations() {
            return this.$api.get(`/clients/${this.client.id}/settings/locations`).then(resp => {
                this.locations = resp
            })
        },
        getPaygroups() {
            return this.$api.get(`/clients/${this.client.id}/settings/paygroups`).then(resp => {
                this.paygroups = resp
            })
        },
        getBankAccounts() {
            return this.$api.get(`/clients/${this.client.id}/settings/bank-accounts`).then(resp => {
                this.bankAccounts = resp
            })
        },
        getDeliveries() {
            return this.$api.get(`/clients/${this.client.id}/settings/delivery-addresses`).then(resp => {
                this.deliveries = resp
            })
        },
        getData() {
            this.$store.dispatch('START_LOADING')
            const promises = [
                this.getOrgs(),
                this.getLocations(),
                this.getPaygroups(),
                this.getBankAccounts(),
                this.getDeliveries(),
            ]

            Promise.all(promises).then(() => {
                this.$store.dispatch('STOP_LOADING')
            }).catch(err => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError('Could not load form. Please refresh the page and try again.')
            })
        },
        makeFormData() {
            const data = {
                org_level_1_value: null,
                org_level_2_value: null,
                org_level_3_value: null,
                org_level_4_value: null,
                org_level_5_value: null,
                work_location: null,
                paygroup: null,
                delivery_address: 'default',
                bank_account_net_pay: null,
                bank_account_taxes: null,
                is_regularly_tipped: false,
            }

            return data
        },
        resetErrors() {
            this.errors =  {}
            this.formErrors = []
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/settings/positions`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated')
                this.close()

            }).catch((errors) => {
                console.log(errors)
                this.$store.dispatch('STOP_LOADING')

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
    }
}
</script>
